import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Trophy, Shield } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { AIContent } from "../components/AIContent";
import { EventList } from "../components/EventList";
import { T } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeagueDetailPage() {
  const { leagueId } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    axios.get(`${API}/leagues/${leagueId}`).then((r) => {
      setData(r.data);
      axios.post(`${API}/track`, { type: "League", name: r.data.league.name, link: `/leagues/${leagueId}` }).catch(() => {});
    }).catch(() => setNotFound(true));
  }, [leagueId]);

  if (notFound) return <div className="py-24 text-center text-slate-500">League not found. <Link to="/teams" className="text-[#0055FF] underline">Back to Teams & Leagues</Link></div>;
  if (!data) return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-4 border-[#0055FF] border-t-transparent" /></div>;

  const { league, teams, sport, events } = data;
  const facts = [
    ["Sport", league.sport], ["Country", league.country], ["Level", league.level],
    ["Organizer", league.organizer], ["Founded", league.founded],
    ...(league.teams_count > 0 ? [["Teams", league.teams_count]] : []),
    ["Season", league.season],
  ];

  return (
    <div>
      <PageHero topic={`${league.name} ${league.sport} competition`} title={league.name} subtitle={`${league.level} ${league.sport} competition organized by ${league.organizer} — season runs ${league.season}.`} listenTargetId="page-content" />
      <div id="page-content" className="mx-auto max-w-7xl space-y-10 px-4 py-12 sm:px-6">
        <Link to="/teams" data-testid="back-to-leagues" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0055FF] hover:underline"><ArrowLeft className="h-4 w-4" /> <T>All Teams & Leagues</T></Link>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="league-facts">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0055FF] text-white"><Trophy className="h-5 w-5" /></span>
              <h2 className="text-lg font-bold text-slate-900"><T>Competition Facts</T></h2>
            </div>
            <dl className="mt-4 space-y-3">
              {facts.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-slate-100 pb-2 text-sm">
                  <dt className="font-bold text-slate-500"><T>{k}</T></dt>
                  <dd className="text-right text-slate-800">{v}</dd>
                </div>
              ))}
            </dl>
            {sport && <p className="mt-4 text-xs text-slate-500"><span className="font-bold"><T>Governing body</T>:</span> <a href={sport.governing_body.url} target="_blank" rel="noreferrer" className="text-[#0055FF] hover:underline">{sport.governing_body.name}</a></p>}
          </div>
          <div className="lg:col-span-2">
            <AIContent topic={`History and structure of the ${league.name}`} context={`Editorial about the ${league.name}, a ${league.level} ${league.sport} competition in ${league.country}, founded ${league.founded}, organized by ${league.organizer}. Cover origins, format, culture and global impact. Do not invent specific results or financial figures.`} />
          </div>
        </div>

        {teams.length > 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="league-teams">
            <h2 className="mb-4 text-xl font-bold text-slate-900"><T>Member Teams on SportsPulse</T></h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {teams.map((t) => (
                <Link key={t.id} to={`/teams/${t.id}`} data-testid={`league-team-link-${t.id}`} className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 transition-all hover:border-[#0055FF] hover:shadow-sm">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-purple-600 text-white"><Shield className="h-4 w-4" /></span>
                  <div>
                    <p className="text-sm font-bold text-slate-900">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.city} · {t.venue}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="mb-4 text-xl font-bold text-slate-900"><T>Related Events</T></h2>
          <EventList events={events} />
          <p className="mt-3 text-[11px] italic text-slate-400"><T>Curated demo data — clearly labeled, not a live feed.</T></p>
        </div>
      </div>
    </div>
  );
}
